import React, { useState, useEffect } from 'react';
import { Clock, User, FileText, Wrench, Users, Bike, TrendingUp, RefreshCw, Key, Search, Filter, ChevronRight, ChevronLeft } from 'lucide-react';
import { auditAPI } from '../api';

const ENTITY_ICONS = {
  ot: Wrench,
  client: Users,
  moto: Bike,
  lead: TrendingUp,
  technicien: User,
  license: Key,
  auth: User,
};

const ENTITY_COLORS = {
  ot: 'var(--accent)',
  client: 'var(--blue)',
  moto: 'var(--green)',
  lead: 'var(--purple)',
  technicien: 'var(--yellow)',
  license: 'var(--orange)',
  auth: 'var(--text3)',
};

const ACTION_LABELS = {
  create: { label: 'Création', color: 'var(--green)', badge: 'badge-green' },
  update: { label: 'Modification', color: 'var(--blue)', badge: 'badge-blue' },
  delete: { label: 'Suppression', color: 'var(--red)', badge: 'badge-red' },
  login: { label: 'Connexion', color: 'var(--green)', badge: 'badge-green' },
  login_failed: { label: 'Échec connexion', color: 'var(--red)', badge: 'badge-red' },
  export: { label: 'Export', color: 'var(--purple)', badge: 'badge-purple' },
};

const ENTITY_LABELS = {
  ot: 'Ordre de Travail',
  client: 'Client',
  moto: 'Moto',
  lead: 'Lead',
  technicien: 'Technicien',
  license: 'Licence',
  auth: 'Authentification',
};

const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('fr-CH', { 
    day: '2-digit', 
    month: '2-digit', 
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatTimeAgo = (dateStr) => {
  const date = new Date(dateStr);
  const now = new Date();
  const diff = Math.floor((now - date) / 1000);
  
  if (diff < 60) return 'À l\'instant';
  if (diff < 3600) return `Il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `Il y a ${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `Il y a ${Math.floor(diff / 86400)} jours`;
  return formatDate(dateStr);
};

const ChangesBadge = ({ changes }) => {
  const [expanded, setExpanded] = useState(false);
  
  if (!changes || Object.keys(changes).length === 0) return null;
  
  const entries = Object.entries(changes);
  
  return (
    <div className="mt-2">
      <button 
        className="text-xs text-[var(--accent)] hover:underline flex items-center gap-1"
        onClick={() => setExpanded(!expanded)}
      >
        {entries.length} champ(s) modifié(s)
        <ChevronRight size={12} className={`transition-transform ${expanded ? 'rotate-90' : ''}`} />
      </button>
      {expanded && (
        <div className="mt-2 p-2 bg-[var(--bg3)] rounded text-xs space-y-1">
          {entries.map(([field, change]) => (
            <div key={field} className="flex gap-2">
              <span className="font-medium text-[var(--text2)]">{field}:</span>
              <span className="text-[var(--red)] line-through">{JSON.stringify(change.old)}</span>
              <span>→</span>
              <span className="text-[var(--green)]">{JSON.stringify(change.new)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const AuditLogView = () => {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    entity_type: '',
    action: '',
    search: '',
  });
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const limit = 50;

  useEffect(() => {
    loadData();
  }, [page, filters.entity_type, filters.action]);

  const loadData = async () => {
    setLoading(true);
    try {
      const params = {
        limit,
        offset: page * limit,
      };
      if (filters.entity_type) params.entity_type = filters.entity_type;
      if (filters.action) params.action = filters.action;
      
      const [logsRes, statsRes] = await Promise.all([
        auditAPI.getLogs(params),
        auditAPI.getStats()
      ]);
      
      setLogs(logsRes.data.logs);
      setTotal(logsRes.data.total);
      setStats(statsRes.data);
    } catch (err) {
      console.error('Error loading audit logs:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredLogs = logs.filter(log => {
    if (filters.search) {
      const search = filters.search.toLowerCase();
      return (
        log.entity_ref?.toLowerCase().includes(search) ||
        log.user_email?.toLowerCase().includes(search) ||
        log.details?.toLowerCase().includes(search)
      );
    }
    return true;
  });

  const totalPages = Math.ceil(total / limit);

  return (
    <div data-testid="audit-log-view">
      {/* Stats Cards */}
      {stats && (
        <div className="stats-grid mb-6">
          <div className="stat-card">
            <div className="stat-label">Total logs</div>
            <div className="stat-value">{stats.total}</div>
            <div className="stat-sub">Depuis le début</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Dernières 24h</div>
            <div className="stat-value" style={{ color: 'var(--green)' }}>{stats.recent_24h}</div>
            <div className="stat-sub">Actions récentes</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Créations</div>
            <div className="stat-value" style={{ color: 'var(--green)' }}>{stats.by_action?.create || 0}</div>
            <div className="stat-sub">Nouveaux éléments</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Modifications</div>
            <div className="stat-value" style={{ color: 'var(--blue)' }}>{stats.by_action?.update || 0}</div>
            <div className="stat-sub">Éléments mis à jour</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-4 mb-6 flex-wrap">
        <div className="flex items-center gap-2 flex-1 min-w-[200px]">
          <Search size={16} className="text-[var(--text3)]" />
          <input
            type="text"
            className="input flex-1"
            placeholder="Rechercher..."
            value={filters.search}
            onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
            data-testid="audit-search"
          />
        </div>
        <select
          className="input"
          value={filters.entity_type}
          onChange={(e) => { setFilters(prev => ({ ...prev, entity_type: e.target.value })); setPage(0); }}
          data-testid="audit-filter-entity"
        >
          <option value="">Tous les types</option>
          <option value="ot">Ordres de Travail</option>
          <option value="client">Clients</option>
          <option value="moto">Motos</option>
          <option value="lead">Leads</option>
          <option value="license">Licences</option>
          <option value="auth">Authentification</option>
        </select>
        <select
          className="input"
          value={filters.action}
          onChange={(e) => { setFilters(prev => ({ ...prev, action: e.target.value })); setPage(0); }}
          data-testid="audit-filter-action"
        >
          <option value="">Toutes les actions</option>
          <option value="create">Créations</option>
          <option value="update">Modifications</option>
          <option value="delete">Suppressions</option>
          <option value="login">Connexions</option>
        </select>
        <button className="btn btn-ghost" onClick={loadData}>
          <RefreshCw size={14} /> Actualiser
        </button>
      </div>

      {/* Logs List */}
      <div className="card">
        <div className="card-header">
          <span className="card-title">Historique des modifications</span>
          <span className="badge badge-gray">{total} entrées</span>
        </div>
        <div className="card-body p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="animate-spin" size={24} />
            </div>
          ) : filteredLogs.length === 0 ? (
            <div className="text-center py-12 text-[var(--text3)]">
              <FileText size={48} className="mx-auto mb-4 opacity-50" />
              <p>Aucun log trouvé</p>
            </div>
          ) : (
            <div className="divide-y divide-[var(--border)]">
              {filteredLogs.map(log => {
                const Icon = ENTITY_ICONS[log.entity_type] || FileText;
                const entityColor = ENTITY_COLORS[log.entity_type] || 'var(--text3)';
                const actionInfo = ACTION_LABELS[log.action] || { label: log.action, badge: 'badge-gray' };
                
                return (
                  <div key={log.id} className="p-4 hover:bg-[var(--bg2)] transition-colors" data-testid={`audit-log-${log.id}`}>
                    <div className="flex items-start gap-4">
                      {/* Icon */}
                      <div 
                        className="p-2 rounded-lg flex-shrink-0"
                        style={{ background: `${entityColor}20` }}
                      >
                        <Icon size={18} style={{ color: entityColor }} />
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`badge ${actionInfo.badge} text-xs`}>
                            {actionInfo.label}
                          </span>
                          <span className="text-sm font-medium">
                            {ENTITY_LABELS[log.entity_type] || log.entity_type}
                          </span>
                          {log.entity_ref && (
                            <span className="text-sm font-mono text-[var(--accent)]">
                              {log.entity_ref}
                            </span>
                          )}
                        </div>
                        
                        {log.details && (
                          <p className="text-sm text-[var(--text2)] mt-1">{log.details}</p>
                        )}
                        
                        <ChangesBadge changes={log.changes} />
                        
                        <div className="flex items-center gap-4 mt-2 text-xs text-[var(--text3)]">
                          {log.user_name && (
                            <span className="flex items-center gap-1">
                              <User size={12} />
                              {log.user_name}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <Clock size={12} />
                            {formatTimeAgo(log.timestamp)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between p-4 border-t border-[var(--border)]">
            <span className="text-sm text-[var(--text3)]">
              Page {page + 1} sur {totalPages}
            </span>
            <div className="flex gap-2">
              <button
                className="btn btn-ghost btn-sm"
                disabled={page === 0}
                onClick={() => setPage(p => p - 1)}
              >
                <ChevronLeft size={14} /> Précédent
              </button>
              <button
                className="btn btn-ghost btn-sm"
                disabled={page >= totalPages - 1}
                onClick={() => setPage(p => p + 1)}
              >
                Suivant <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuditLogView;
